import React, { useId } from 'react';

/** Shared patient/Studio healthcare tunic renderer. Body and trim are
 * independently colourable; provider branding remains a separate approved asset. */
export default function LayeredTunicSvg({
  body = '#F5F7FA',
  trim = '#1676D2',
  showBadge = true,
  provider = 'nhs',
  className = '',
  title,
}) {
  const rawId = useId().replace(/:/g, '');
  const ids = {
    bodyHighlight: `tunic-body-highlight-${rawId}`,
    bodyShade: `tunic-body-shade-${rawId}`,
    trimGloss: `tunic-trim-gloss-${rawId}`,
    garmentClip: `tunic-garment-clip-${rawId}`,
    logo: `tunic-nhs-logo-${rawId}`,
  };
  const badgeVisible = showBadge && provider === 'nhs';

  const outline = 'M499 18 L426 88 L383 108 L280 147 L264 155 L249 169 L195 244 L60 458 L60 461 L81 479 L147 524 L216 561 L263 580 L267 579 L307 495 L330 632 L333 671 L331 719 L289 973 L245 1168 L256 1172 L260 1179 L314 1199 L392 1218 L488 1230 L598 1235 L731 1232 L829 1223 L929 1202 L991 1180 L998 1172 L1006 1170 L1009 1163 L982 1057 L964 971 L933 791 L921 703 L921 659 L925 618 L945 497 L985 577 L990 580 L1037 561 L1106 524 L1171 480 L1187 467 L1193 458 L1057 242 L1009 174 L988 154 L829 89 L759 21 L751 17 Z';

  return (
    <svg viewBox="0 0 1254 1254" className={className} role={title ? 'img' : undefined} aria-label={title || undefined} aria-hidden={title ? undefined : true} data-template="healthcare-tunic-v1">
      <defs>
        <linearGradient id={ids.bodyHighlight} x1="0" y1="0" x2="1" y2="0.2">
          <stop offset="0" stopColor="#FFFFFF" stopOpacity="0.50" />
          <stop offset="0.35" stopColor="#FFFFFF" stopOpacity="0.18" />
          <stop offset="1" stopColor="#FFFFFF" stopOpacity="0" />
        </linearGradient>
        <linearGradient id={ids.bodyShade} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#11345A" stopOpacity="0" />
          <stop offset="0.58" stopColor="#11345A" stopOpacity="0.02" />
          <stop offset="1" stopColor="#11345A" stopOpacity="0.10" />
        </linearGradient>
        <linearGradient id={ids.trimGloss} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#FFFFFF" stopOpacity="0.20" />
          <stop offset="1" stopColor="#000000" stopOpacity="0.06" />
        </linearGradient>
        <clipPath id={ids.garmentClip}><path d={outline} /></clipPath>
        <symbol id={ids.logo} viewBox="0 0 200 80">
          <rect width="200" height="80" fill="#fff" />
          <path fill="#005eb8" d="M200 0v80H0V0h200Zm-27.5 5.5c-14.5 0-29 5-29 22 0 10.2 7.7 13.5 14.7 16.3l.7.3c5.4 2 10.1 3.9 10.1 8.4 0 6.5-8.5 7.5-14 7.5s-12.5-1.5-16-3.5L135 70c5.5 2 13.5 3.5 20 3.5 15.5 0 32-4.5 32-22.5 0-19.5-25.5-16.5-25.5-25.5 0-5.5 5.5-6.5 12.5-6.5a35 35 0 0 1 14.5 3l4-13.5c-4.5-2-12-3-20-3Zm-131 2h-22l-14 65H22l9-45h.5l13.5 45h21.5l14-65H64l-9 45h-.5l-13-45Zm63 0h-18l-13 65h17l6-28H117l-5.5 28H129l13.5-65H125L119.5 32h-20l5-24.5Z" />
        </symbol>
      </defs>

      <g id="body" data-colour-role="body">
        <path fill={body} d={outline} />
        <path fill={body} d="M320 836 C382 838 467 838 527 836 L526 1064 Q495 1101 451 1123 Q394 1122 330 1087 Z" />
        <path fill={body} d="M729 836 C789 838 874 838 935 836 L934 1087 Q870 1122 813 1123 Q769 1101 729 1064 Z" />
      </g>

      <g id="shading" data-layer="shading" clipPath={`url(#${ids.garmentClip})`} pointerEvents="none">
        <rect x="36" y="0" width="1188" height="1254" fill={`url(#${ids.bodyHighlight})`} />
        <rect x="36" y="0" width="1188" height="1254" fill={`url(#${ids.bodyShade})`} />
        <path d="M307 170 C338 323 343 548 326 862" fill="none" stroke="#FFFFFF" strokeOpacity="0.22" strokeWidth="18" strokeLinecap="round" />
        <path d="M947 170 C916 323 911 548 928 862" fill="none" stroke="#173652" strokeOpacity="0.055" strokeWidth="17" strokeLinecap="round" />
        <path d="M501 350 C486 580 486 865 499 1128" fill="none" stroke="#FFFFFF" strokeOpacity="0.13" strokeWidth="12" strokeLinecap="round" />
        <path d="M753 350 C768 580 768 865 755 1128" fill="none" stroke="#173652" strokeOpacity="0.035" strokeWidth="11" strokeLinecap="round" />
      </g>

      <g id="neck">
        <path d="M509 58 Q627 26 745 58 L711 178 L628 304 L543 178 Z" fill="#DDE6F0" />
        <path d="M512 69 Q627 42 742 69" fill="none" stroke="#91A3B7" strokeWidth="1.6" opacity="0.7" />
      </g>

      <g id="trim" data-colour-role="trim">
        <path fill={trim} d="M449 69 L431 85 L428 93 L420 136 L416 173 L416 191 L421 196 L437 200 L485 207 L512 209 L518 195 L436 185 L431 179 L438 114 L443 87 Z" />
        <path fill={trim} d="M806 70 L815 110 L823 179 L818 185 L736 195 L742 209 L817 200 L833 196 L837 192 L838 180 L834 139 L825 91 L821 83 Z" />
        <path fill={trim} d="M511 216 L504 231 L504 240 L510 247 L622 328 L646 350 L637 325 L521 237 L513 228 Z" />
        <path fill={trim} d="M743 216 L741 227 L734 236 L634 312 L638 323 L724 262 L748 243 L750 233 Z" />
        <path fill={trim} d="M97 402 L90 416 L124 443 L167 471 L209 495 L258 519 L282 528 L287 532 L294 516 L287 511 L255 498 L197 468 L135 429 L99 401 Z" />
        <path fill={trim} d="M1156 402 L1154 401 L1109 435 L1054 469 L998 498 L973 508 L968 512 L959 516 L965 528 L968 530 L1004 515 L1048 493 L1107 458 L1163 417 L1163 414 Z" />
        <path fill={trim} d="M320 836 C382 838 467 838 527 836 L517 856 C456 859 389 859 331 856 Z" />
        <path fill={trim} d="M729 836 C789 838 874 838 935 836 L924 856 C865 859 799 859 740 856 Z" />
        <g opacity="0.32">
          <path fill={`url(#${ids.trimGloss})`} d="M449 69 L431 85 L428 93 L420 136 L416 173 L416 191 L421 196 L437 200 L485 207 L512 209 L518 195 L436 185 L431 179 L438 114 L443 87 Z" />
          <path fill={`url(#${ids.trimGloss})`} d="M806 70 L815 110 L823 179 L818 185 L736 195 L742 209 L817 200 L833 196 L837 192 L838 180 L834 139 L825 91 L821 83 Z" />
          <path fill={`url(#${ids.trimGloss})`} d="M320 836 C382 838 467 838 527 836 L517 856 C456 859 389 859 331 856 Z" />
          <path fill={`url(#${ids.trimGloss})`} d="M729 836 C789 838 874 838 935 836 L924 856 C865 859 799 859 740 856 Z" />
        </g>
      </g>

      <g id="seams" fill="none" strokeLinecap="round" strokeLinejoin="round">
        <path d={outline} stroke="#173B62" strokeWidth="1.65" />
        <path d="M628 308 L628 1233" stroke="#879AAF" strokeWidth="1.35" opacity="0.70" />
        <path d="M321 159 C356 313 368 534 356 834" stroke="#879AAF" strokeWidth="1.35" opacity="0.63" />
        <path d="M933 159 C898 313 886 534 898 834" stroke="#879AAF" strokeWidth="1.35" opacity="0.63" />
        <path d="M318 1176 Q470 1211 628 1211 Q786 1211 936 1176" stroke="#8B9CB0" strokeWidth="1.35" opacity="0.62" />
        <path d="M317 1192 Q470 1225 628 1225 Q786 1225 937 1192" stroke="#A6B3C1" strokeWidth="1" opacity="0.52" />
        <path d="M320 836 C382 838 467 838 527 836 L526 1064 Q495 1101 451 1123 Q394 1122 330 1087 Z" stroke="#244B74" strokeWidth="2.1" />
        <path d="M729 836 C789 838 874 838 935 836 L934 1087 Q870 1122 813 1123 Q769 1101 729 1064 Z" stroke="#244B74" strokeWidth="2.1" />
        <path d="M331 856 C389 859 456 859 517 856 L516 1054 Q482 1086 450 1101 Q399 1100 340 1070 Z" stroke="#A2B0BF" strokeWidth="1.25" opacity="0.72" />
        <path d="M740 856 C799 859 865 859 924 856 L925 1070 Q866 1100 815 1101 Q783 1086 740 1054 Z" stroke="#A2B0BF" strokeWidth="1.25" opacity="0.72" />
      </g>

      {badgeVisible ? <g id="provider-logo" data-provider="nhs" data-placement="left_chest" aria-hidden="true">
        <svg x="750" y="348" width="112" height="49" viewBox="0 0 200 80" overflow="visible">
          <use href={`#${ids.logo}`} width="200" height="80" />
        </svg>
      </g> : null}
    </svg>
  );
}
